import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import { FileText, Plus, Search, Download, ChevronDown, ChevronRight, Check, X, Clock, Send, Eye, Trash2, Edit, Copy, ArrowRight, LayoutDashboard, Receipt, PoundSterling, AlertTriangle, Filter, MoreVertical, MapPin, Settings, Save, Layers } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ConfirmModal from '../../components/modals/ConfirmModal';
import ErrorModal from '../../components/modals/ErrorModal';

// ============================================================
// HELPERS
// ============================================================
const STATUS_COLORS = {
  draft: 'bg-slate-500/20 text-slate-300',
  sent: 'bg-blue-500/20 text-blue-300',
  accepted: 'bg-green-500/20 text-green-300',
  declined: 'bg-red-500/20 text-red-300',
  expired: 'bg-amber-500/20 text-amber-300',
  paid: 'bg-emerald-500/20 text-emerald-300',
  overdue: 'bg-red-500/20 text-red-300',
  void: 'bg-slate-500/20 text-slate-400',
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const computeLineTotals = (items) => {
  let subtotal = 0, totalVat = 0;
  (items || []).forEach(item => {
    const net = Math.round(item.quantity * item.unit_price * (1 - item.discount / 100) * 100) / 100;
    const vat = Math.round(net * item.vat_rate / 100 * 100) / 100;
    subtotal += net;
    totalVat += vat;
  });
  return { subtotal: Math.round(subtotal * 100) / 100, totalVat: Math.round(totalVat * 100) / 100, total: Math.round((subtotal + totalVat) * 100) / 100 };
};

// ============================================================
// SUB-COMPONENTS
// ============================================================

const StatCard = ({ label, value, icon: Icon, color = 'purple', badge }) => (
  <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-slate-400 text-sm flex items-center gap-2">{label}{badge != null && <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold bg-${color}-500/20 text-${color}-400`}>{badge}</span>}</span>
      <div className={`p-2 rounded-lg bg-${color}-500/20`}>
        <Icon className={`w-4 h-4 text-${color}-400`} />
      </div>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

const StatusBadge = ({ status }) => (
  <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[status] || STATUS_COLORS.draft}`} data-testid={`status-badge-${status}`}>
    {status}
  </span>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="p-4 bg-slate-800 rounded-2xl mb-4">
      <Icon className="w-8 h-8 text-slate-500" />
    </div>
    <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
    <p className="text-slate-400 text-sm mb-4 max-w-md">{description}</p>
    {action}
  </div>
);


// ============================================================
// INVOICE CHART
// ============================================================
const InvoiceChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0]?.payload || {};
  const total = (data.paid || 0) + (data.overdue || 0) + (data.pending || 0);
  return (
    <div style={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', padding: '12px', fontSize: '13px' }}>
      <p style={{ color: '#e2e8f0', fontWeight: 600, marginBottom: '8px' }}>{label}</p>
      <p style={{ color: '#fff', fontWeight: 700, marginBottom: '6px' }}>Total: {formatCurrency(total)}</p>
      <p style={{ color: '#a78bfa', margin: '2px 0' }}>Paid: {formatCurrency(data.paid || 0)}</p>
      <p style={{ color: '#6b7280', margin: '2px 0' }}>Pending: {formatCurrency(data.pending || 0)}</p>
      <p style={{ color: '#f87171', margin: '2px 0' }}>Overdue: {formatCurrency(data.overdue || 0)}</p>
    </div>
  );
};

const InvoiceChart = ({ data }) => {
  const totals = data.reduce((acc, d) => ({
    paid: acc.paid + d.paid,
    overdue: acc.overdue + d.overdue,
    pending: acc.pending + d.pending,
  }), { paid: 0, overdue: 0, pending: 0 });

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
        <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={{ stroke: '#475569' }} tickLine={false} />
        <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={{ stroke: '#475569' }} tickLine={false} tickFormatter={(v) => `£${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
        <Tooltip content={<InvoiceChartTooltip />} cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }} />
        <Legend wrapperStyle={{ paddingTop: '12px' }} content={() => {
          const items = [
            { name: 'Paid', total: totals.paid, color: '#a78bfa', fmt: true },
            { name: 'Pending', total: totals.pending, color: '#6b7280', fmt: true },
            { name: 'Overdue', total: totals.overdue, color: '#f87171', fmt: true },
          ];
          return (
            <ul style={{ display: 'flex', listStyle: 'none', padding: 0, margin: 0, justifyContent: 'center', gap: '16px' }}>
              {items.map((item) => (
                <li key={item.name} style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                  <svg width={8} height={8} style={{ marginRight: '6px' }}><circle cx={4} cy={4} r={4} fill={item.color} /></svg>
                  <span style={{ color: '#94a3b8' }}>{item.name} [{formatCurrency(item.total)}]</span>
                </li>
              ))}
            </ul>
          );
        }} />
        <Bar dataKey="paid" name="Paid" stackId="invoices" fill="#a78bfa" />
        <Bar dataKey="pending" name="Pending" stackId="invoices" fill="#6b7280" />
        <Bar dataKey="overdue" name="Overdue" stackId="invoices" fill="#f87171" radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// ============================================================
// QUOTE CHART
// ============================================================
const QuoteChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0]?.payload || {};
  return (
    <div style={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', padding: '12px', fontSize: '13px' }}>
      <p style={{ color: '#e2e8f0', fontWeight: 600, marginBottom: '8px' }}>{label}</p>
      <p style={{ color: '#a78bfa', margin: '2px 0' }}>Accepted: {data.accepted || 0}</p>
      <p style={{ color: '#6b7280', margin: '2px 0' }}>Pending: {data.pending || 0}</p>
      <p style={{ color: '#f87171', margin: '2px 0' }}>Declined / Expired: {data.declined_expired || 0}</p>
    </div>
  );
};

const QuoteChart = ({ data }) => {
  const merged = data.map(d => ({ ...d, declined_expired: (d.declined || 0) + (d.expired || 0) }));
  const totals = merged.reduce((acc, d) => ({
    accepted: acc.accepted + d.accepted,
    pending: acc.pending + d.pending,
    declined_expired: acc.declined_expired + d.declined_expired,
  }), { accepted: 0, pending: 0, declined_expired: 0 });

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={merged} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
        <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={{ stroke: '#475569' }} tickLine={false} />
        <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={{ stroke: '#475569' }} tickLine={false} allowDecimals={false} />
        <Tooltip content={<QuoteChartTooltip />} cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }} />
        <Legend wrapperStyle={{ paddingTop: '12px' }} content={() => {
          const items = [
            { name: 'Accepted', total: totals.accepted, color: '#a78bfa' },
            { name: 'Pending', total: totals.pending, color: '#6b7280' },
            { name: 'Declined / Expired', total: totals.declined_expired, color: '#f87171' },
          ];
          return (
            <ul style={{ display: 'flex', listStyle: 'none', padding: 0, margin: 0, justifyContent: 'center', gap: '16px' }}>
              {items.map((item) => (
                <li key={item.name} style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                  <svg width={8} height={8} style={{ marginRight: '6px' }}><circle cx={4} cy={4} r={4} fill={item.color} /></svg>
                  <span style={{ color: '#94a3b8' }}>{item.name} [{item.total}]</span>
                </li>
              ))}
            </ul>
          );
        }} />
        <Bar dataKey="accepted" name="Accepted" stackId="quotes" fill="#a78bfa" />
        <Bar dataKey="pending" name="Pending" stackId="quotes" fill="#6b7280" />
        <Bar dataKey="declined_expired" name="Declined / Expired" stackId="quotes" fill="#f87171" radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// ============================================================
// LINE ITEM EDITOR
// ============================================================
const numInputClass = "bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

const LineItemEditor = ({ items, onChange }) => {
  const addItem = () => {
    onChange([...items, { description: '', quantity: 1, unit_price: 0, vat_rate: 20, discount: 0 }]);
  };
  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };
  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };
  const totals = computeLineTotals(items);

  return (
    <div className="space-y-3" data-testid="line-item-editor">
      <div className="grid grid-cols-[1fr_60px_90px_60px_60px_80px_80px_36px] gap-2 text-xs font-medium text-slate-400 items-end">
        <span className="pl-3">Description</span><span className="text-center">Qty</span><span className="text-right pr-3">Unit Price</span><span className="text-center">Disc %</span><span className="text-center">VAT %</span><span className="text-right">Net</span><span className="text-right">Total</span><span></span>
      </div>
      {items.map((item, i) => {
        const net = Math.round(item.quantity * item.unit_price * (1 - item.discount / 100) * 100) / 100;
        const vat = Math.round(net * item.vat_rate / 100 * 100) / 100;
        return (
          <div key={i} className="grid grid-cols-[1fr_60px_90px_60px_60px_80px_80px_36px] gap-2 items-center" data-testid={`line-item-${i}`}>
            <input className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" placeholder="Description" value={item.description} onChange={(e) => updateItem(i, 'description', e.target.value)} data-testid={`line-item-desc-${i}`} />
            <input className={`${numInputClass} text-center`} type="number" min="0" step="1" value={item.quantity} onChange={(e) => updateItem(i, 'quantity', parseFloat(e.target.value) || 0)} />
            <input className={`${numInputClass} text-right`} type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => updateItem(i, 'unit_price', parseFloat(e.target.value) || 0)} />
            <input className={`${numInputClass} text-center`} type="number" min="0" max="100" value={item.discount} onChange={(e) => updateItem(i, 'discount', parseFloat(e.target.value) || 0)} />
            <input className={`${numInputClass} text-center`} type="number" min="0" max="100" value={item.vat_rate} onChange={(e) => updateItem(i, 'vat_rate', parseFloat(e.target.value) || 0)} />
            <span className="text-sm text-slate-300 text-right">{formatCurrency(net)}</span>
            <span className="text-sm text-white font-medium text-right">{formatCurrency(net + vat)}</span>
            <button onClick={() => removeItem(i)} className="p-1.5 text-slate-500 hover:text-red-400 transition-colors rounded-lg hover:bg-slate-700" data-testid={`remove-line-item-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        );
      })}
      <button onClick={addItem} className="flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300 transition-colors mt-2" data-testid="add-line-item-btn">
        <Plus className="w-4 h-4" /> Add Line Item
      </button>
      <div className="border-t border-slate-700 pt-3 mt-3 space-y-1">
        <div className="flex justify-between text-sm"><span className="text-slate-400">Subtotal</span><span className="text-white">{formatCurrency(totals.subtotal)}</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-400">VAT</span><span className="text-white">{formatCurrency(totals.totalVat)}</span></div>
        <div className="flex justify-between text-base font-bold border-t border-slate-600 pt-2 mt-2"><span className="text-white">Total</span><span className="text-white">{formatCurrency(totals.total)}</span></div>
      </div>
    </div>
  );
};

// ============================================================
// SETUP MODAL (Notes & Terms Builder)
// ============================================================
const SettingsToggleItem = ({ label, enabled, onToggle, children }) => (
  <div className="border border-slate-700 rounded-lg overflow-hidden">
    <button type="button" onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-700/30 transition-colors">
      <span className="text-sm text-white font-medium">{label}</span>
      <div className={`w-9 h-5 rounded-full transition-colors relative ${enabled ? 'bg-purple-600' : 'bg-slate-600'}`}>
        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </div>
    </button>
    {enabled && <div className="px-4 pb-3 space-y-2 border-t border-slate-700/50 pt-3">{children}</div>}
  </div>
);

const SettingsTextInput = ({ value, onChange, placeholder, label }) => (
  <div>
    {label && <label className="block text-xs text-slate-400 mb-1">{label}</label>}
    <input className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  </div>
);

const SettingsTextArea = ({ value, onChange, placeholder }) => (
  <textarea className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white h-16 resize-none" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
);

const DEFAULTS = {
  quote_terms: {
    payment_terms: { text: "Full payment is required within 30 days of written acceptance of this quotation. A deposit of 50% may be required before work commences." },
    validity: { text: "This quotation is valid for the number of days stated above from the date of issue. After this period, prices may be subject to revision." },
    scope_of_work: { text: "This quotation covers only the works and materials explicitly described in the line items above. Any variations, additional works, or unforeseen requirements will be quoted and agreed separately before being carried out." },
    exclusions: { text: "Unless expressly stated, this quotation excludes: VAT (charged at the prevailing rate), building control and planning application fees, removal of hazardous materials (e.g. asbestos), and any works not described above." },
  },
  quote_notes: {
    general: { text: "Thank you for the opportunity to provide this quotation. We are confident in delivering high-quality workmanship and look forward to working with you. Please do not hesitate to contact us if you have any questions." },
  },
  invoice_terms: {
    payment_due: { text: "Payment of the full amount is due by the date shown above. Please use the invoice number as your payment reference." },
    late_payment: { text: "We reserve the right to charge interest on overdue payments at 8% per annum above the Bank of England base rate, plus compensation as permitted under the Late Payment of Commercial Debts (Interest) Act 1998." },
  },
  invoice_notes: {
    general: { text: "Thank you for your business. If you have any queries regarding this invoice, please contact us promptly quoting the invoice number above." },
    vat_note: { text: "All amounts are subject to VAT at the prevailing rate unless otherwise stated. Our VAT registration number is shown on this invoice." },
  },
};

// ── Template List Item (inside Setup modal) ──
const TemplateListItem = ({ template, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const totals = computeLineTotals(template.line_items || []);
  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden" data-testid={`template-item-${template.id}`}>
      <div className="flex items-center justify-between px-4 py-3 hover:bg-slate-700/30 transition-colors cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          <div>
            <span className="text-sm text-white font-medium">{template.name}</span>
            <span className="text-xs text-slate-500 ml-2">{template.item_count || template.line_items?.length || 0} items</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400">{formatCurrency(totals.total)}</span>
          <button onClick={(e) => { e.stopPropagation(); onEdit(template); }} className="p-1.5 text-slate-400 hover:text-purple-400 transition-colors" data-testid={`edit-template-${template.id}`}><Edit className="w-3.5 h-3.5" /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(template.id); }} className="p-1.5 text-slate-400 hover:text-red-400 transition-colors" data-testid={`delete-template-${template.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-3 border-t border-slate-700/50 pt-3">
          <div className="space-y-1.5">
            {(template.line_items || []).map((item, i) => (
              <div key={i} className="flex justify-between text-xs text-slate-400">
                <span className="truncate flex-1 mr-4">{item.description || '(no description)'}</span>
                <span className="whitespace-nowrap">Qty: {item.quantity} x {formatCurrency(item.unit_price)}</span>
              </div>
            ))}
          </div>
          {template.notes && <p className="text-xs text-slate-500 mt-2 border-t border-slate-700/30 pt-2">Notes: {template.notes.substring(0, 100)}{template.notes.length > 100 ? '...' : ''}</p>}
        </div>
      )}
    </div>
  );
};

// ── Template Edit Form (inline in Setup modal) ──
const TemplateEditForm = ({ template, templateType, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: template?.name || '',
    type: templateType,
    line_items: template?.line_items || [{ description: '', quantity: 1, unit_price: 0, vat_rate: 20, discount: 0 }],
    notes: template?.notes || '',
    terms: template?.terms || '',
    valid_days: template?.valid_days || 30,
    due_days: template?.due_days || 30,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await onSave({ ...form, ...(template?.id ? { id: template.id } : {}) });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="template-edit-form">
      <div>
        <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Template Name *</label>
        <input className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" placeholder="e.g. Bathroom Renovation" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name-input" />
      </div>
      <div>
        <label className="block text-xs uppercase text-slate-500 font-semibold mb-2">Line Items</label>
        <LineItemEditor items={form.line_items} onChange={(items) => setForm({ ...form, line_items: items })} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Default Notes</label>
          <textarea className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white h-16 resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional default notes" />
        </div>
        <div>
          <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Default Terms</label>
          <textarea className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white h-16 resize-none" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} placeholder="Optional default terms" />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onCancel} className="px-4 py-2 text-slate-300 hover:text-white transition-colors text-sm">Cancel</button>
        <button onClick={handleSave} disabled={saving || !form.name.trim()} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 text-white rounded-lg transition-colors text-sm" data-testid="template-save-btn">{saving ? 'Saving...' : (template?.id ? 'Update Template' : 'Create Template')}</button>
      </div>
    </div>
  );
};

const SetupModal = ({ settings, templates, onSave, onSaveTemplate, onDeleteTemplate, onClose }) => {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('quotes');
  const [editingTemplate, setEditingTemplate] = useState(null); // null | 'new' | template object
  const [templateTabType, setTemplateTabType] = useState(null); // tracks which template tab triggered edit

  const update = (section, key, field, value) => {
    setForm(f => ({ ...f, [section]: { ...f[section], [key]: { ...f[section]?.[key], [field]: value } } }));
  };
  const toggle = (section, key) => {
    const current = form[section]?.[key]?.enabled ?? false;
    if (!current) {
      // Turning ON — populate with default text if field is empty
      const existing = form[section]?.[key] || {};
      const defaults = DEFAULTS[section]?.[key] || {};
      const merged = { ...existing, enabled: true };
      if (defaults.text && !existing.text) merged.text = defaults.text;
      setForm(f => ({ ...f, [section]: { ...f[section], [key]: merged } }));
    } else {
      update(section, key, 'enabled', false);
    }
  };
  const get = (section, key, field) => form[section]?.[key]?.[field] ?? '';

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(form); onClose(); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="setup-modal">
      <div className={`bg-slate-800 rounded-xl border border-slate-700 w-full ${activeTab.includes('templates') ? 'max-w-4xl' : 'max-w-2xl'} max-h-[90vh] flex flex-col`}>
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Setup & Templates</h2>
            <p className="text-sm text-slate-400 mt-0.5">Configure defaults and manage reusable templates</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="border-b border-slate-700 px-5">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { key: 'quotes', label: 'Quote Setup' },
              { key: 'invoices', label: 'Invoice Setup' },
              { key: 'quote_templates', label: 'Quote Templates' },
              { key: 'invoice_templates', label: 'Invoice Templates' },
            ].map(t => (
              <button key={t.key} onClick={() => { setActiveTab(t.key); setEditingTemplate(null); }} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === t.key ? 'border-purple-500 text-purple-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`} data-testid={`setup-tab-${t.key}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {activeTab === 'quotes' && (
            <>
              <div>
                <h3 className="text-xs uppercase text-slate-500 font-semibold tracking-wider mb-3">Terms & Conditions</h3>
                <div className="space-y-2">
                  <SettingsToggleItem label="Payment Terms" enabled={get('quote_terms', 'payment_terms', 'enabled')} onToggle={() => toggle('quote_terms', 'payment_terms')}>
                    <SettingsTextArea value={get('quote_terms', 'payment_terms', 'text')} onChange={(v) => update('quote_terms', 'payment_terms', 'text', v)} placeholder="Full payment is required within 30 days of written acceptance..." />
                  </SettingsToggleItem>
                  <SettingsToggleItem label="Validity Period" enabled={get('quote_terms', 'validity', 'enabled')} onToggle={() => toggle('quote_terms', 'validity')}>
                    <SettingsTextArea value={get('quote_terms', 'validity', 'text')} onChange={(v) => update('quote_terms', 'validity', 'text', v)} placeholder="This quotation is valid for the number of days stated above..." />
                  </SettingsToggleItem>
                  <SettingsToggleItem label="Scope of Work" enabled={get('quote_terms', 'scope_of_work', 'enabled')} onToggle={() => toggle('quote_terms', 'scope_of_work')}>
                    <SettingsTextArea value={get('quote_terms', 'scope_of_work', 'text')} onChange={(v) => update('quote_terms', 'scope_of_work', 'text', v)} placeholder="This quotation covers only the works and materials explicitly described..." />
                  </SettingsToggleItem>
                  <SettingsToggleItem label="Exclusions" enabled={get('quote_terms', 'exclusions', 'enabled')} onToggle={() => toggle('quote_terms', 'exclusions')}>
                    <SettingsTextArea value={get('quote_terms', 'exclusions', 'text')} onChange={(v) => update('quote_terms', 'exclusions', 'text', v)} placeholder="Unless expressly stated, this quotation excludes: VAT, building control fees..." />
                  </SettingsToggleItem>
                  <SettingsToggleItem label="Custom Clause" enabled={get('quote_terms', 'custom', 'enabled')} onToggle={() => toggle('quote_terms', 'custom')}>
                    <SettingsTextArea value={get('quote_terms', 'custom', 'text')} onChange={(v) => update('quote_terms', 'custom', 'text', v)} placeholder="Enter custom terms..." />
                  </SettingsToggleItem>
                </div>
              </div>
              <div>
                <h3 className="text-xs uppercase text-slate-500 font-semibold tracking-wider mb-3">Notes</h3>
                <div className="space-y-2">
                  <SettingsToggleItem label="General Notes" enabled={get('quote_notes', 'general', 'enabled')} onToggle={() => toggle('quote_notes', 'general')}>
                    <SettingsTextArea value={get('quote_notes', 'general', 'text')} onChange={(v) => update('quote_notes', 'general', 'text', v)} placeholder="Thank you for the opportunity to provide this quotation..." />
                  </SettingsToggleItem>
                  <SettingsToggleItem label="Custom Note" enabled={get('quote_notes', 'custom', 'enabled')} onToggle={() => toggle('quote_notes', 'custom')}>
                    <SettingsTextArea value={get('quote_notes', 'custom', 'text')} onChange={(v) => update('quote_notes', 'custom', 'text', v)} placeholder="Enter custom note..." />
                  </SettingsToggleItem>
                </div>
              </div>
            </>
          )}

          {activeTab === 'invoices' && (
            <>
              <div>
                <h3 className="text-xs uppercase text-slate-500 font-semibold tracking-wider mb-3">Terms & Conditions</h3>
                <div className="space-y-2">
                  <SettingsToggleItem label="Payment Due Date" enabled={get('invoice_terms', 'payment_due', 'enabled')} onToggle={() => toggle('invoice_terms', 'payment_due')}>
                    <SettingsTextArea value={get('invoice_terms', 'payment_due', 'text')} onChange={(v) => update('invoice_terms', 'payment_due', 'text', v)} placeholder="Payment of the full amount is due by the date shown above..." />
                  </SettingsToggleItem>
                  <SettingsToggleItem label="Late Payment Terms" enabled={get('invoice_terms', 'late_payment', 'enabled')} onToggle={() => toggle('invoice_terms', 'late_payment')}>
                    <SettingsTextArea value={get('invoice_terms', 'late_payment', 'text')} onChange={(v) => update('invoice_terms', 'late_payment', 'text', v)} placeholder="We reserve the right to charge interest on overdue payments at 8% per annum..." />
                  </SettingsToggleItem>
                  <SettingsToggleItem label="Custom Clause" enabled={get('invoice_terms', 'custom', 'enabled')} onToggle={() => toggle('invoice_terms', 'custom')}>
                    <SettingsTextArea value={get('invoice_terms', 'custom', 'text')} onChange={(v) => update('invoice_terms', 'custom', 'text', v)} placeholder="Enter custom terms..." />
                  </SettingsToggleItem>
                </div>
              </div>
              <div>
                <h3 className="text-xs uppercase text-slate-500 font-semibold tracking-wider mb-3">Notes</h3>
                <div className="space-y-2">
                  <SettingsToggleItem label="Bank Details" enabled={get('invoice_notes', 'bank_details', 'enabled')} onToggle={() => toggle('invoice_notes', 'bank_details')}>
                    <div className="grid grid-cols-2 gap-2">
                      <SettingsTextInput label="Bank Name" value={get('invoice_notes', 'bank_details', 'bank_name')} onChange={(v) => update('invoice_notes', 'bank_details', 'bank_name', v)} placeholder="e.g. Barclays" />
                      <SettingsTextInput label="Account Name" value={get('invoice_notes', 'bank_details', 'account_name')} onChange={(v) => update('invoice_notes', 'bank_details', 'account_name', v)} placeholder="e.g. Acme Ltd" />
                      <SettingsTextInput label="Account Number" value={get('invoice_notes', 'bank_details', 'account_number')} onChange={(v) => update('invoice_notes', 'bank_details', 'account_number', v)} placeholder="e.g. 12345678" />
                      <SettingsTextInput label="Sort Code" value={get('invoice_notes', 'bank_details', 'sort_code')} onChange={(v) => update('invoice_notes', 'bank_details', 'sort_code', v)} placeholder="e.g. 20-00-00" />
                    </div>
                  </SettingsToggleItem>
                  <SettingsToggleItem label="VAT Note" enabled={get('invoice_notes', 'vat_note', 'enabled')} onToggle={() => toggle('invoice_notes', 'vat_note')}>
                    <SettingsTextArea value={get('invoice_notes', 'vat_note', 'text')} onChange={(v) => update('invoice_notes', 'vat_note', 'text', v)} placeholder="All amounts are subject to VAT at the prevailing rate unless otherwise stated..." />
                  </SettingsToggleItem>
                  <SettingsToggleItem label="General Notes" enabled={get('invoice_notes', 'general', 'enabled')} onToggle={() => toggle('invoice_notes', 'general')}>
                    <SettingsTextArea value={get('invoice_notes', 'general', 'text')} onChange={(v) => update('invoice_notes', 'general', 'text', v)} placeholder="Thank you for your business. If you have any queries regarding this invoice..." />
                  </SettingsToggleItem>
                  <SettingsToggleItem label="Custom Note" enabled={get('invoice_notes', 'custom', 'enabled')} onToggle={() => toggle('invoice_notes', 'custom')}>
                    <SettingsTextArea value={get('invoice_notes', 'custom', 'text')} onChange={(v) => update('invoice_notes', 'custom', 'text', v)} placeholder="Enter custom note..." />
                  </SettingsToggleItem>
                </div>
              </div>
            </>
          )}

          {(activeTab === 'quote_templates' || activeTab === 'invoice_templates') && (() => {
            const tplType = activeTab === 'quote_templates' ? 'quote' : 'invoice';
            const filtered = (templates || []).filter(t => t.type === tplType);

            if (editingTemplate) {
              return (
                <TemplateEditForm
                  template={editingTemplate === 'new' ? null : editingTemplate}
                  templateType={tplType}
                  onSave={async (data) => { await onSaveTemplate(data); setEditingTemplate(null); }}
                  onCancel={() => setEditingTemplate(null)}
                />
              );
            }

            return (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-400">{filtered.length} template{filtered.length !== 1 ? 's' : ''}</p>
                  <button onClick={() => setEditingTemplate('new')} className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition-colors" data-testid="create-template-btn">
                    <Plus className="w-4 h-4" /> New Template
                  </button>
                </div>
                {filtered.length === 0 ? (
                  <div className="text-center py-10">
                    <Layers className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">No {tplType} templates yet</p>
                    <p className="text-slate-500 text-xs mt-1">Create a template to quickly populate line items when creating new {tplType}s</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filtered.map(t => (
                      <TemplateListItem
                        key={t.id}
                        template={t}
                        onEdit={(tpl) => setEditingTemplate(tpl)}
                        onDelete={async (id) => { await onDeleteTemplate(id); }}
                      />
                    ))}
                  </div>
                )}
              </>
            );
          })()}

        </div>

        {(activeTab === 'quotes' || activeTab === 'invoices') && (
          <div className="p-4 border-t border-slate-700 flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 text-slate-300 hover:text-white transition-colors">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 text-white rounded-lg transition-colors" data-testid="setup-save-btn">{saving ? 'Saving...' : 'Save Settings'}</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// COMPILE SETTINGS INTO NOTES/TERMS TEXT
// ============================================================
function compileTerms(settings, docType) {
  const section = docType === 'quote' ? settings?.quote_terms : settings?.invoice_terms;
  if (!section) return '';
  const lines = [];
  Object.entries(section).forEach(([key, item]) => {
    if (!item?.enabled || !item?.text) return;
    lines.push(item.text);
  });
  return lines.join('\n\n');
}

function compileNotes(settings, docType) {
  const section = docType === 'quote' ? settings?.quote_notes : settings?.invoice_notes;
  if (!section) return '';
  const lines = [];
  Object.entries(section).forEach(([key, item]) => {
    if (!item?.enabled) return;
    if (key === 'bank_details') {
      const parts = [];
      if (item.bank_name) parts.push(`Bank: ${item.bank_name}`);
      if (item.account_name) parts.push(`Account Name: ${item.account_name}`);
      if (item.account_number) parts.push(`Account No: ${item.account_number}`);
      if (item.sort_code) parts.push(`Sort Code: ${item.sort_code}`);
      if (parts.length) lines.push(parts.join('\n'));
    } else if (item.text) {
      lines.push(item.text);
    }
  });
  return lines.join('\n\n');
}

// ============================================================
// CLIENT FORM MODAL
// ============================================================
// ============================================================
// QUOTE / INVOICE FORM MODAL
// ============================================================
const DocumentFormModal = ({ doc, docType, clients, sites, orgData, quotingSettings, templates, onSaveTemplate, onSave, onClose }) => {
  const isQuote = docType === 'quote';
  const isEdit = !!doc;
  const numberField = isQuote ? 'quote_number' : 'invoice_number';

  // For new docs, pre-populate notes/terms from settings
  const defaultTerms = !doc ? compileTerms(quotingSettings, isQuote ? 'quote' : 'invoice') : '';
  const defaultNotes = !doc ? compileNotes(quotingSettings, isQuote ? 'quote' : 'invoice') : '';

  const [form, setForm] = useState({
    client_id: doc?.client_id || '',
    site_id: doc?.site_id || '',
    [numberField]: doc?.[numberField] || '',
    reference: doc?.reference || '',
    line_items: doc?.line_items || [{ description: '', quantity: 1, unit_price: 0, vat_rate: 20, discount: 0 }],
    notes: doc?.notes || defaultNotes,
    terms: doc?.terms || defaultTerms,
    ...(isQuote ? { valid_days: doc?.valid_days || 30 } : { due_days: doc?.due_days || 30 }),
  });
  const [saving, setSaving] = useState(false);
  const [numberError, setNumberError] = useState('');
  const [saveAsTemplateName, setSaveAsTemplateName] = useState('');
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    if (!isEdit) {
      const token = localStorage.getItem('token');
      axios.get(`${API}/quoting/next-number`, { params: { type: isQuote ? 'quote' : 'invoice' }, headers: { Authorization: `Bearer ${token}` } })
        .then(res => setForm(f => ({ ...f, [numberField]: res.data.number })))
        .catch(() => {});
    }
  }, [isEdit, isQuote, numberField]);

  const selectedSite = form.site_id ? sites.find(s => s.id === form.site_id) : null;
  const siteHasClient = selectedSite && selectedSite.client_id;
  const siteMissingClient = selectedSite && !selectedSite.client_id;
  const selectedClient = form.client_id ? clients.find(c => c.id === form.client_id) : null;

  const handleSiteChange = (siteId) => {
    if (!siteId) { setForm({ ...form, site_id: '', client_id: '' }); return; }
    const site = sites.find(s => s.id === siteId);
    setForm({ ...form, site_id: siteId, client_id: site?.client_id || '' });
  };

  const handleSave = async () => {
    if (!form.client_id) return;
    setNumberError('');
    setSaving(true);
    try {
      await onSave(form);
    } catch (err) {
      if (err?.response?.status === 409) { setNumberError(err.response.data?.detail || 'This number already exists'); return; }
      throw err;
    } finally { setSaving(false); }
  };

  const handleApplyTemplate = (templateId) => {
    if (!templateId) return;
    const tpl = (templates || []).find(t => t.id === templateId);
    if (!tpl) return;
    const newItems = (tpl.line_items || []).map(li => ({
      description: li.description || '',
      quantity: li.quantity || 1,
      unit_price: li.unit_price || 0,
      vat_rate: li.vat_rate ?? 20,
      discount: li.discount || 0,
    }));
    // Append to existing — remove empty placeholder rows first
    const existing = form.line_items.filter(li => li.description || li.unit_price > 0);
    setForm({ ...form, line_items: [...existing, ...newItems] });
  };

  const handleSaveAsTemplate = async () => {
    if (!saveAsTemplateName.trim()) return;
    setSavingTemplate(true);
    try {
      await onSaveTemplate({
        name: saveAsTemplateName.trim(),
        type: isQuote ? 'quote' : 'invoice',
        line_items: form.line_items.filter(li => li.description || li.unit_price > 0),
        notes: form.notes,
        terms: form.terms,
        valid_days: form.valid_days || 30,
        due_days: form.due_days || 30,
      });
      setShowSaveAsTemplate(false);
      setSaveAsTemplateName('');
    } finally { setSavingTemplate(false); }
  };

  const clientLabel = (c) => c?.company_name || `${c?.first_name || ''} ${c?.last_name || ''}`.trim() || '-';
  const todayStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const totals = computeLineTotals(form.line_items);

  // Org details
  const orgName = orgData?.name || orgData?.company_name || '';
  const orgAddress = [orgData?.address_line_1, orgData?.address_line_2, orgData?.city, orgData?.postcode].filter(Boolean).join(', ') || orgData?.city || '';
  const orgEmail = orgData?.email || '';
  const orgPhone = orgData?.phone || '';
  const orgLogo = orgData?.logo || orgData?.logo_url || '';

  // Client details for "Bill To"
  const clientAddress = selectedClient ? [selectedClient.address_line_1, selectedClient.address_line_2, selectedClient.city, selectedClient.postcode].filter(Boolean).join(', ') : '';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="document-form-modal">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto">

        {/* ── Header: Logo + Title + Org Details ── */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-4">
              {orgLogo && <img src={orgLogo} alt="Logo" className="h-12 max-w-[160px] object-contain rounded" />}
              <div>
                <h2 className="text-xl font-bold text-white tracking-tight">{isQuote ? 'QUOTATION' : 'INVOICE'}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    className={`bg-transparent border-b text-base font-mono text-purple-400 focus:outline-none w-40 ${numberError ? 'border-red-500' : 'border-slate-600 focus:border-purple-500'}`}
                    value={form[numberField]}
                    onChange={(e) => { setForm({ ...form, [numberField]: e.target.value }); setNumberError(''); }}
                    disabled={isEdit}
                    placeholder={isQuote ? 'QUO-0000' : 'INV-0000'}
                    data-testid="doc-number"
                  />
                </div>
                {numberError && <p className="text-xs text-red-400 mt-1">{numberError}</p>}
              </div>
            </div>
            <div className="text-right text-sm space-y-0.5">
              <p className="text-white font-semibold">{orgName}</p>
              {orgAddress && <p className="text-slate-400">{orgAddress}</p>}
              {orgEmail && <p className="text-slate-400">{orgEmail}</p>}
              {orgPhone && <p className="text-slate-400">{orgPhone}</p>}
              <button onClick={onClose} className="mt-2 p-1.5 text-slate-500 hover:text-white rounded-lg hover:bg-slate-700 transition-colors"><X className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">

          {/* ── Selectors: Site + Client ── */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Site</label>
              <select className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" value={form.site_id} onChange={(e) => handleSiteChange(e.target.value)} data-testid="doc-site-select">
                <option value="">No site linked</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.site_name}</option>)}
              </select>
              {siteMissingClient && <p className="text-xs text-amber-400 mt-1">This site has no client linked. Please update client details in the Clients menu first.</p>}
            </div>
            <div>
              <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Client *</label>
              {siteHasClient ? (
                <div className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-300 cursor-not-allowed" data-testid="doc-client-locked">{clientLabel(selectedClient)}</div>
              ) : (
                <select className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} disabled={siteMissingClient} data-testid="doc-client-select">
                  <option value="">Select client...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{clientLabel(c)}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Reference</label>
              <input className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" placeholder="e.g. Bathroom refit" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="doc-reference" />
            </div>
          </div>

          {/* ── Details Panel (mirrors PDF grey row) ── */}
          <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-4 flex justify-between gap-6">
            <div className="flex-1">
              <h4 className="text-xs uppercase text-slate-500 font-semibold mb-2">Bill To</h4>
              {selectedClient ? (
                <div className="space-y-0.5 text-sm">
                  <p className="text-white font-medium">{clientLabel(selectedClient)}</p>
                  {selectedClient.first_name && selectedClient.company_name && <p className="text-slate-400">{selectedClient.first_name} {selectedClient.last_name}</p>}
                  {clientAddress && <p className="text-slate-400">{clientAddress}</p>}
                  {selectedClient.email && <p className="text-slate-400">{selectedClient.email}</p>}
                  {selectedClient.vat_number && <p className="text-slate-400">VAT: {selectedClient.vat_number}</p>}
                </div>
              ) : (
                <p className="text-sm text-slate-600 italic">Select a client above</p>
              )}
            </div>
            <div className="text-right text-sm space-y-1.5 min-w-[180px]">
              <div className="flex justify-between"><span className="text-slate-500">Date</span><span className="text-white">{todayStr}</span></div>
              <div className="flex justify-between gap-3 items-center">
                <span className="text-slate-500">{isQuote ? 'Valid (days)' : 'Due (days)'}</span>
                <input type="number" min="1" className={`${numInputClass} w-16 text-center text-white`} value={isQuote ? form.valid_days : form.due_days} onChange={(e) => setForm({ ...form, [isQuote ? 'valid_days' : 'due_days']: parseInt(e.target.value) || 30 })} data-testid="doc-days" />
              </div>
              <div className="flex justify-between"><span className="text-slate-500">Status</span><span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded-full text-xs font-medium">{isEdit ? (doc?.status || 'draft').toUpperCase() : 'DRAFT'}</span></div>
            </div>
          </div>

          {/* ── Line Items ── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs uppercase text-slate-500 font-semibold">Line Items</h4>
              {(templates || []).length > 0 && (
                <select
                  className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-xs text-slate-300"
                  value=""
                  onChange={(e) => handleApplyTemplate(e.target.value)}
                  data-testid="template-selector"
                >
                  <option value="">Apply template...</option>
                  {(templates || []).map(t => (
                    <option key={t.id} value={t.id}>{t.name} ({t.item_count || t.line_items?.length || 0} items)</option>
                  ))}
                </select>
              )}
            </div>
            <LineItemEditor items={form.line_items} onChange={(items) => setForm({ ...form, line_items: items })} />
          </div>

          {/* ── Notes & Terms ── */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs uppercase text-slate-500 font-semibold mb-1">Notes</h4>
              <textarea className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white h-20 resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="doc-notes" />
            </div>
            <div>
              <h4 className="text-xs uppercase text-slate-500 font-semibold mb-1">Terms & Conditions</h4>
              <textarea className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white h-20 resize-none" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} data-testid="doc-terms" />
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="p-4 border-t border-slate-700">
          {showSaveAsTemplate ? (
            <div className="flex items-center gap-2 mb-3" data-testid="save-as-template-form">
              <input
                className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white"
                placeholder="Template name, e.g. Bathroom Renovation"
                value={saveAsTemplateName}
                onChange={(e) => setSaveAsTemplateName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveAsTemplate()}
                autoFocus
                data-testid="save-as-template-name"
              />
              <button onClick={handleSaveAsTemplate} disabled={savingTemplate || !saveAsTemplateName.trim()} className="px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 text-white rounded-lg text-sm transition-colors" data-testid="save-as-template-confirm">{savingTemplate ? 'Saving...' : 'Save'}</button>
              <button onClick={() => { setShowSaveAsTemplate(false); setSaveAsTemplateName(''); }} className="px-3 py-2 text-slate-400 hover:text-white text-sm">Cancel</button>
            </div>
          ) : null}
          <div className="flex justify-between">
            <button onClick={() => setShowSaveAsTemplate(true)} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-purple-400 transition-colors" data-testid="save-as-template-btn">
              <Save className="w-4 h-4" /> Save as Template
            </button>
            <div className="flex gap-3">
              <button onClick={onClose} className="px-4 py-2 text-slate-300 hover:text-white transition-colors" data-testid="doc-form-cancel">Cancel</button>
              <button onClick={handleSave} disabled={saving || !form.client_id || siteMissingClient} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 text-white rounded-lg transition-colors flex items-center gap-2" data-testid="doc-form-save">{saving ? 'Saving...' : `Save ${isQuote ? 'Quote' : 'Invoice'}`}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// DOCUMENT DETAIL MODAL (View / Actions)
// ============================================================
const DocumentDetailModal = ({ doc, docType, orgData, sites, pdfLoading, onClose, onStatusChange, onConvertToInvoice, onDownloadPdf, onDuplicate, onEdit, onDelete }) => {
  const isQuote = docType === 'quote';
  const cs = doc.client_snapshot || {};
  const totals = computeLineTotals(doc.line_items || []);
  const [showActions, setShowActions] = useState(false);
  const [markPaidForm, setMarkPaidForm] = useState(null);

  const orgName = orgData?.name || orgData?.company_name || '';
  const orgAddress = [orgData?.address_line_1, orgData?.address_line_2, orgData?.city, orgData?.postcode].filter(Boolean).join(', ') || orgData?.city || '';
  const orgEmail = orgData?.email || '';
  const orgPhone = orgData?.phone || '';
  const orgLogo = orgData?.logo || orgData?.logo_url || '';
  const siteName = doc.site_id ? sites?.find(s => s.id === doc.site_id)?.site_name : null;

  const clientName = cs.company_name || `${cs.first_name || ''} ${cs.last_name || ''}`.trim() || doc.inline_client_name || '-';
  const clientContact = cs.first_name && cs.company_name ? `${cs.first_name} ${cs.last_name}`.trim() : '';
  const clientAddress = [cs.address_line_1, cs.address_line_2, cs.city, cs.postcode].filter(Boolean).join(', ') || doc.inline_client_address || '';

  const statusActions = isQuote
    ? [
        doc.status === 'draft' && { label: 'Mark as Sent', status: 'sent', icon: Send },
        doc.status === 'sent' && { label: 'Mark Accepted', status: 'accepted', icon: Check },
        doc.status === 'sent' && { label: 'Mark Declined', status: 'declined', icon: X },
      ].filter(Boolean)
    : [
        doc.status === 'draft' && { label: 'Mark as Sent', status: 'sent', icon: Send },
        (doc.status === 'sent' || doc.status === 'overdue') && { label: 'Mark as Paid', status: 'paid', icon: Check },
      ].filter(Boolean);

  // Header menu items by status
  const canEdit = isQuote
    ? ['draft', 'sent'].includes(doc.status)
    : ['draft', 'sent', 'overdue'].includes(doc.status);
  const canDelete = isQuote
    ? ['draft', 'sent', 'declined', 'expired'].includes(doc.status)
    : true;
  const canDownloadPdf = isQuote
    ? ['draft', 'sent', 'accepted', 'declined', 'expired'].includes(doc.status)
    : true;
  const canConvertToInvoice = isQuote && doc.status === 'accepted';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="document-detail-modal">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">

        {pdfLoading && (
          <div className="absolute inset-0 bg-slate-900/80 z-10 flex items-center justify-center rounded-xl">
            <div className="flex items-center gap-3 bg-slate-800 border border-slate-700 rounded-lg px-5 py-3">
              <svg className="w-5 h-5 animate-spin text-purple-400" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
              <span className="text-sm text-white font-medium">Generating PDF...</span>
            </div>
          </div>
        )}

        {/* ── Header: Logo + Title + Org Details ── */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-4">
              {orgLogo && <img src={orgLogo} alt="Logo" className="h-12 max-w-[160px] object-contain rounded" />}
              <div>
                <h2 className="text-xl font-bold text-white tracking-tight">{isQuote ? 'QUOTATION' : 'INVOICE'}</h2>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-base font-mono text-purple-400">{isQuote ? doc.quote_number : doc.invoice_number}</span>
                  <StatusBadge status={doc.status} />
                  {isQuote && <span className="text-xs text-slate-500">v{doc.version}</span>}
                </div>
                {doc.reference && <p className="text-sm text-slate-400 mt-0.5">{doc.reference}</p>}
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="text-right text-sm space-y-0.5">
                <p className="text-white font-semibold">{orgName}</p>
                {orgAddress && <p className="text-slate-400">{orgAddress}</p>}
                {orgEmail && <p className="text-slate-400">{orgEmail}</p>}
                {orgPhone && <p className="text-slate-400">{orgPhone}</p>}
              </div>
              <div className="flex items-center gap-1 ml-2">
                <div className="relative">
                  <button onClick={() => setShowActions(!showActions)} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors" data-testid="doc-actions-menu"><MoreVertical className="w-5 h-5" /></button>
                  {showActions && (
                    <div className="absolute right-0 top-full mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-xl z-10 py-1 min-w-[180px]" data-testid="doc-actions-dropdown">
                      {canEdit && <button onClick={() => { onEdit(doc); setShowActions(false); }} className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 flex items-center gap-2"><Edit className="w-4 h-4" /> Edit</button>}
                      <button onClick={() => { onDuplicate(doc); setShowActions(false); }} className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 flex items-center gap-2" data-testid="duplicate-doc-btn"><Copy className="w-4 h-4" /> Duplicate</button>
                      {canDownloadPdf && <button onClick={() => { onDownloadPdf(doc); setShowActions(false); }} disabled={pdfLoading} className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 flex items-center gap-2 disabled:opacity-50">
                        {pdfLoading ? <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg> Generating PDF...</> : <><Download className="w-4 h-4" /> Download PDF</>}
                      </button>}
                      {canConvertToInvoice && <button onClick={() => { onConvertToInvoice(doc); setShowActions(false); }} className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 flex items-center gap-2"><ArrowRight className="w-4 h-4" /> Convert to Invoice</button>}
                      {canDelete && <><div className="border-t border-slate-600 my-1"></div><button onClick={() => { onDelete(doc); setShowActions(false); }} className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-slate-600 flex items-center gap-2"><Trash2 className="w-4 h-4" /> Delete</button></>}
                    </div>
                  )}
                </div>
                <button onClick={onClose} className="p-2 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">

          {/* ── Details Panel ── */}
          <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-4 flex justify-between gap-6">
            <div className="flex-1">
              <h4 className="text-xs uppercase text-slate-500 font-semibold mb-2">Bill To</h4>
              <div className="space-y-0.5 text-sm">
                <p className="text-white font-medium">{clientName}</p>
                {clientContact && <p className="text-slate-400">{clientContact}</p>}
                {clientAddress && <p className="text-slate-400">{clientAddress}</p>}
                {cs.email && <p className="text-slate-400">{cs.email}</p>}
                {cs.vat_number && <p className="text-slate-400">VAT: {cs.vat_number}</p>}
              </div>
            </div>
            <div className="text-right text-sm space-y-1.5 min-w-[200px]">
              <div className="flex justify-between"><span className="text-slate-500">Date</span><span className="text-white">{formatDate(doc.created_at)}</span></div>
              {isQuote && <div className="flex justify-between"><span className="text-slate-500">Valid Until</span><span className="text-white">{formatDate(doc.valid_until)}</span></div>}
              {!isQuote && <div className="flex justify-between"><span className="text-slate-500">Due Date</span><span className="text-white">{formatDate(doc.due_date)}</span></div>}
              {siteName && <div className="flex justify-between"><span className="text-slate-500">Site</span><span className="text-white">{siteName}</span></div>}
              {!isQuote && doc.source_quote_number && <div className="flex justify-between"><span className="text-slate-500">From Quote</span><span className="text-purple-400">{doc.source_quote_number}</span></div>}
            </div>
          </div>

          {/* ── Line Items ── */}
          <div className="border border-slate-700 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-700/50">
                <th className="text-left px-4 py-2.5 text-slate-400 font-medium">Description</th>
                <th className="text-center px-3 py-2.5 text-slate-400 font-medium">Qty</th>
                <th className="text-right px-3 py-2.5 text-slate-400 font-medium">Price</th>
                <th className="text-center px-3 py-2.5 text-slate-400 font-medium">Disc</th>
                <th className="text-right px-3 py-2.5 text-slate-400 font-medium">Net</th>
                <th className="text-center px-3 py-2.5 text-slate-400 font-medium">VAT %</th>
                <th className="text-right px-3 py-2.5 text-slate-400 font-medium">VAT</th>
                <th className="text-right px-4 py-2.5 text-slate-400 font-medium">Total</th>
              </tr></thead>
              <tbody>
                {(doc.line_items || []).map((item, i) => {
                  const net = Math.round(item.quantity * item.unit_price * (1 - item.discount / 100) * 100) / 100;
                  const vat = Math.round(net * item.vat_rate / 100 * 100) / 100;
                  return (
                    <tr key={i} className="border-t border-slate-700/50">
                      <td className="px-4 py-2.5 text-white">{item.description}</td>
                      <td className="px-3 py-2.5 text-slate-300 text-center">{item.quantity}</td>
                      <td className="px-3 py-2.5 text-slate-300 text-right">{formatCurrency(item.unit_price)}</td>
                      <td className="px-3 py-2.5 text-slate-300 text-center">{item.discount}%</td>
                      <td className="px-3 py-2.5 text-slate-300 text-right">{formatCurrency(net)}</td>
                      <td className="px-3 py-2.5 text-slate-300 text-center">{item.vat_rate}%</td>
                      <td className="px-3 py-2.5 text-slate-300 text-right">{formatCurrency(vat)}</td>
                      <td className="px-4 py-2.5 text-white font-medium text-right">{formatCurrency(net + vat)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex justify-end">
              <div className="min-w-[250px] bg-slate-700/30 px-4 py-3 space-y-1">
                <div className="flex justify-between text-sm"><span className="text-slate-400">Subtotal</span><span className="text-white">{formatCurrency(totals.subtotal)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-400">VAT</span><span className="text-white">{formatCurrency(totals.totalVat)}</span></div>
                <div className="flex justify-between text-base font-bold border-t border-slate-600 pt-2 mt-1"><span className="text-white">Total</span><span className="text-white">{formatCurrency(totals.total)}</span></div>
              </div>
            </div>
          </div>

          {/* ── Notes / Terms ── */}
          {(doc.notes || doc.terms) && (
            <div className="grid grid-cols-2 gap-4">
              {doc.notes && <div><h4 className="text-xs uppercase text-slate-500 font-semibold mb-1">Notes</h4><p className="text-sm text-slate-300 whitespace-pre-wrap">{doc.notes}</p></div>}
              {doc.terms && <div><h4 className="text-xs uppercase text-slate-500 font-semibold mb-1">Terms & Conditions</h4><p className="text-sm text-slate-300 whitespace-pre-wrap">{doc.terms}</p></div>}
            </div>
          )}

          {/* ── Payment info ── */}
          {!isQuote && doc.status === 'paid' && (
            <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-lg p-4">
              <h4 className="text-xs uppercase text-emerald-400 font-semibold mb-2">Payment Received</h4>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><span className="text-slate-400">Date</span><p className="text-white">{formatDate(doc.payment_date) || formatDate(doc.updated_at)}</p></div>
                <div><span className="text-slate-400">Method</span><p className="text-white">{doc.payment_method || '-'}</p></div>
                <div><span className="text-slate-400">Reference</span><p className="text-white">{doc.payment_reference || '-'}</p></div>
              </div>
            </div>
          )}

          {/* ── Version History ── */}
          {isQuote && doc.version_history && doc.version_history.length > 0 && (
            <div>
              <h4 className="text-xs uppercase text-slate-500 font-semibold mb-2">Version History</h4>
              <div className="space-y-1">
                {doc.version_history.map((v, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm bg-slate-900/50 rounded-lg px-3 py-2 border border-slate-700">
                    <span className="text-slate-400">v{v.version}</span>
                    <StatusBadge status={v.status} />
                    <span className="text-slate-300">{formatCurrency(v.total)}</span>
                    <span className="text-slate-500 ml-auto text-xs">{formatDate(v.archived_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Status Actions ── */}
          {statusActions.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-700">
              {statusActions.map(action => {
                if (action.status === 'paid' && !isQuote) {
                  return markPaidForm ? (
                    <div key="mark-paid" className="flex items-center gap-2 bg-slate-900/50 border border-slate-700 rounded-lg p-3 w-full">
                      <input placeholder="Method (e.g. Bank Transfer)" className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white flex-1" value={markPaidForm.method} onChange={(e) => setMarkPaidForm({ ...markPaidForm, method: e.target.value })} data-testid="mark-paid-method" />
                      <input placeholder="Reference" className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white flex-1" value={markPaidForm.reference} onChange={(e) => setMarkPaidForm({ ...markPaidForm, reference: e.target.value })} data-testid="mark-paid-reference" />
                      <button onClick={() => { onStatusChange(doc, 'paid', markPaidForm); setMarkPaidForm(null); }} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg" data-testid="mark-paid-confirm">Confirm</button>
                      <button onClick={() => setMarkPaidForm(null)} className="px-3 py-1.5 text-slate-400 hover:text-white text-sm">Cancel</button>
                    </div>
                  ) : (
                    <button key={action.label} onClick={() => setMarkPaidForm({ method: '', reference: '' })} className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 rounded-lg text-sm transition-colors" data-testid="mark-paid-btn">
                      <action.icon className="w-4 h-4" />{action.label}
                    </button>
                  );
                }
                return (
                  <button key={action.label} onClick={() => onStatusChange(doc, action.status)} className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 text-slate-300 hover:bg-slate-600 rounded-lg text-sm transition-colors" data-testid={`status-action-${action.status}`}>
                    <action.icon className="w-4 h-4" />{action.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN PAGE COMPONENT
// ============================================================
const AdminQuotingPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);

  // Data
  const [dashboard, setDashboard] = useState(null);
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [sites, setSites] = useState([]);
  const [orgData, setOrgData] = useState(null);
  const [quotingSettings, setQuotingSettings] = useState(null);
  const [templates, setTemplates] = useState([]);

  // Chart data
  const [invoiceChartData, setInvoiceChartData] = useState([]);
  const [invoiceChartPeriod, setInvoiceChartPeriod] = useState('6m');
  const [quoteChartData, setQuoteChartData] = useState([]);
  const [quoteChartPeriod, setQuoteChartPeriod] = useState('6m');

  // Modals
  const [documentModal, setDocumentModal] = useState(null); // null | { type, doc? }
  const [detailModal, setDetailModal] = useState(null); // null | { type, doc }
  const [setupModal, setSetupModal] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');

  // Action modals
  const [confirmAction, setConfirmAction] = useState(null); // { title, message, onConfirm, variant }
  const [errorMessage, setErrorMessage] = useState(null); // { title, message }
  const [pdfLoading, setPdfLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // ---- FETCH ----
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quoting/dashboard`, { headers });
      setDashboard(res.data);
    } catch (err) { console.error('Dashboard fetch error:', err); }
  }, []);

  const fetchClients = useCallback(async (search) => {
    try {
      const params = search ? { search } : {};
      const res = await axios.get(`${API}/quoting/clients`, { headers, params });
      setClients(res.data.clients || []);
    } catch (err) { console.error('Clients fetch error:', err); }
  }, []);

  const fetchQuotes = useCallback(async (status, search, client_id, period, site_id) => {
    try {
      const params = {};
      if (status) params.status = status;
      if (search) params.search = search;
      if (client_id) params.client_id = client_id;
      if (period) params.period = period;
      if (site_id) params.site_id = site_id;
      const res = await axios.get(`${API}/quoting/quotes`, { headers, params });
      setQuotes(res.data.quotes || []);
    } catch (err) { console.error('Quotes fetch error:', err); }
  }, []);

  const fetchInvoices = useCallback(async (status, search, client_id, period, site_id) => {
    try {
      const params = {};
      if (status) params.status = status;
      if (search) params.search = search;
      if (client_id) params.client_id = client_id;
      if (period) params.period = period;
      if (site_id) params.site_id = site_id;
      const res = await axios.get(`${API}/quoting/invoices`, { headers, params });
      setInvoices(res.data.invoices || []);
    } catch (err) { console.error('Invoices fetch error:', err); }
  }, []);

  const fetchSites = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/sites`, { headers });
      setSites(res.data || []);
    } catch (err) { console.error('Sites fetch error:', err); }
  }, []);

  const fetchOrgData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/organisation`, { headers });
      setOrgData(res.data || null);
    } catch (err) { console.error('Org data fetch error:', err); }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quoting/settings`, { headers });
      setQuotingSettings(res.data || null);
    } catch (err) { console.error('Settings fetch error:', err); }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quoting/templates`, { headers });
      setTemplates(res.data.templates || []);
    } catch (err) { console.error('Templates fetch error:', err); }
  }, []);

  const fetchInvoiceChart = useCallback(async (period) => {
    try {
      const res = await axios.get(`${API}/quoting/charts/invoices`, { headers, params: { period } });
      setInvoiceChartData(res.data.data || []);
    } catch (err) { console.error('Invoice chart fetch error:', err); }
  }, []);

  const fetchQuoteChart = useCallback(async (period) => {
    try {
      const res = await axios.get(`${API}/quoting/charts/quotes`, { headers, params: { period } });
      setQuoteChartData(res.data.data || []);
    } catch (err) { console.error('Quote chart fetch error:', err); }
  }, []);

  const refreshAll = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([fetchDashboard(), fetchClients(), fetchQuotes(), fetchInvoices(), fetchSites(), fetchOrgData(), fetchSettings(), fetchTemplates(), fetchInvoiceChart(invoiceChartPeriod), fetchQuoteChart(quoteChartPeriod)]);
    setIsLoading(false);
  }, [fetchDashboard, fetchClients, fetchQuotes, fetchInvoices, fetchSites, fetchOrgData, fetchSettings, fetchTemplates, fetchInvoiceChart, invoiceChartPeriod, fetchQuoteChart, quoteChartPeriod]);

  useEffect(() => { refreshAll(); }, []);

  useEffect(() => {
    fetchInvoiceChart(invoiceChartPeriod);
  }, [invoiceChartPeriod, fetchInvoiceChart]);

  useEffect(() => {
    fetchQuoteChart(quoteChartPeriod);
  }, [quoteChartPeriod, fetchQuoteChart]);

  useEffect(() => {
    if (activeTab === 'quotes') fetchQuotes(statusFilter, searchTerm, clientFilter, periodFilter, siteFilter);
    else if (activeTab === 'invoices') fetchInvoices(statusFilter, searchTerm, clientFilter, periodFilter, siteFilter);
  }, [activeTab, statusFilter, searchTerm, clientFilter, periodFilter, siteFilter]);

  // ---- HANDLERS ----
  const handleSaveSettings = async (data) => {
    try {
      await axios.put(`${API}/quoting/settings`, data, { headers });
      fetchSettings();
    } catch (err) { setErrorMessage({ title: 'Save Failed', message: 'Failed to save settings' }); }
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      if (templateData.id) {
        await axios.put(`${API}/quoting/templates/${templateData.id}`, templateData, { headers });
      } else {
        await axios.post(`${API}/quoting/templates`, templateData, { headers });
      }
      fetchTemplates();
    } catch (err) { setErrorMessage({ title: 'Save Failed', message: err.response?.data?.detail || 'Failed to save template' }); }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/quoting/templates/${templateId}`, { headers });
      fetchTemplates();
    } catch (err) { setErrorMessage({ title: 'Delete Failed', message: 'Failed to delete template' }); }
  };

  const handleSaveDocument = async (formData) => {
    const isQuote = documentModal.type === 'quote';
    const endpoint = isQuote ? 'quotes' : 'invoices';
    if (documentModal.doc?.id) {
      await axios.put(`${API}/quoting/${endpoint}/${documentModal.doc.id}`, formData, { headers });
    } else {
      await axios.post(`${API}/quoting/${endpoint}`, formData, { headers });
    }
    setDocumentModal(null);
    setDetailModal(null);
    if (isQuote) fetchQuotes(statusFilter, searchTerm, clientFilter, periodFilter, siteFilter); else fetchInvoices(statusFilter, searchTerm, clientFilter, periodFilter, siteFilter);
    fetchDashboard();
  };

  const handleStatusChange = async (doc, newStatus, paymentInfo) => {
    const isQuote = !!doc.quote_number;
    const endpoint = isQuote ? 'quotes' : 'invoices';
    try {
      if (newStatus === 'paid' && !isQuote) {
        await axios.post(`${API}/quoting/invoices/${doc.id}/mark-paid`, {
          payment_method: paymentInfo?.method || '',
          payment_reference: paymentInfo?.reference || '',
        }, { headers });
      } else {
        await axios.put(`${API}/quoting/${endpoint}/${doc.id}`, { status: newStatus }, { headers });
      }
      setDetailModal(null);
      if (isQuote) fetchQuotes(statusFilter, searchTerm, clientFilter, periodFilter, siteFilter); else fetchInvoices(statusFilter, searchTerm, clientFilter, periodFilter, siteFilter);
      fetchDashboard();
    } catch (err) { setErrorMessage({ title: 'Update Failed', message: err.response?.data?.detail || 'Failed to update status' }); }
  };

  const handleConvertToInvoice = (doc) => {
    setConfirmAction({
      title: 'Convert to Invoice',
      message: `Convert ${doc.quote_number} to a new invoice?`,
      variant: 'info',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          const res = await axios.post(`${API}/quoting/quotes/${doc.id}/convert-to-invoice`, {}, { headers });
          setDetailModal({ type: 'invoice', doc: res.data });
          setActiveTab('invoices');
          fetchInvoices(statusFilter, searchTerm, clientFilter, periodFilter, siteFilter);
          fetchDashboard();
        } catch (err) { setErrorMessage({ title: 'Conversion Failed', message: err.response?.data?.detail || 'Failed to convert' }); }
      }
    });
  };

  const handleDownloadPdf = async (doc) => {
    const isQuote = !!doc.quote_number;
    const endpoint = isQuote ? 'quotes' : 'invoices';
    setPdfLoading(true);
    try {
      const res = await axios.get(`${API}/quoting/${endpoint}/${doc.id}/pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', isQuote ? `${doc.quote_number}_v${doc.version}.pdf` : `${doc.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { setErrorMessage({ title: 'Download Failed', message: 'Failed to download PDF. Please try again.' }); }
    finally { setPdfLoading(false); }
  };

  const handleDeleteDocument = (doc) => {
    const isQuote = !!doc.quote_number;
    const label = isQuote ? doc.quote_number : doc.invoice_number;
    setConfirmAction({
      title: `Delete ${label}`,
      message: `Are you sure you want to delete ${label}? This action cannot be undone.`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmAction(null);
        const endpoint = isQuote ? 'quotes' : 'invoices';
        try {
          await axios.delete(`${API}/quoting/${endpoint}/${doc.id}`, { headers });
          setDetailModal(null);
          if (isQuote) fetchQuotes(statusFilter, searchTerm, clientFilter, periodFilter, siteFilter); else fetchInvoices(statusFilter, searchTerm, clientFilter, periodFilter, siteFilter);
          fetchDashboard();
        } catch (err) { setErrorMessage({ title: 'Delete Failed', message: err.response?.data?.detail || 'Failed to delete' }); }
      }
    });
  };

  const handleDuplicate = async (doc) => {
    const isQuote = !!doc.quote_number;
    const endpoint = isQuote ? 'quotes' : 'invoices';
    const payload = {
      client_id: doc.client_id,
      reference: doc.reference ? `${doc.reference} (Copy)` : '(Copy)',
      line_items: (doc.line_items || []).map(item => ({
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        vat_rate: item.vat_rate,
        discount: item.discount,
      })),
      notes: doc.notes || '',
      terms: doc.terms || '',
      ...(isQuote ? { valid_days: doc.valid_days || 30 } : { due_days: doc.due_days || 30 }),
    };
    try {
      const res = await axios.post(`${API}/quoting/${endpoint}`, payload, { headers });
      setDetailModal({ type: isQuote ? 'quote' : 'invoice', doc: res.data });
      if (isQuote) fetchQuotes(statusFilter, searchTerm, clientFilter, periodFilter, siteFilter); else fetchInvoices(statusFilter, searchTerm, clientFilter, periodFilter, siteFilter);
      fetchDashboard();
    } catch (err) { setErrorMessage({ title: 'Duplicate Failed', message: err.response?.data?.detail || 'Failed to duplicate' }); }
  };

  // ---- TABS ----
  const tabs = [
    { key: 'overview', label: 'Overview', icon: LayoutDashboard },
    { key: 'quotes', label: 'Quotes', icon: FileText },
    { key: 'invoices', label: 'Invoices', icon: Receipt },
  ];

  // ---- RENDER ----
  return (
    <div className="min-h-screen bg-slate-900 -m-8 p-8 space-y-6" data-testid="quoting-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Quotes & Invoices</h1>
        <p className="text-purple-200">Create and manage quotes and invoices for your clients</p>
      </div>

      {/* Tab Bar */}
      <div className="bg-slate-800 rounded-xl border border-slate-700">
        <div className="px-4">
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setSearchTerm(''); setStatusFilter(''); setClientFilter(''); setPeriodFilter(''); setSiteFilter(''); }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.key ? 'border-purple-500 text-purple-400' : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
                data-testid={`tab-${tab.key}`}
              >
                <tab.icon className="w-4 h-4" />{tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <>
            {/* ==================== DASHBOARD ==================== */}
            {activeTab === 'overview' && dashboard && (
              <div className="space-y-6" data-testid="overview-tab">
                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => { setActiveTab('quotes'); setDocumentModal({ type: 'quote' }); }}
                    className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 text-left hover:border-purple-500/50 hover:bg-slate-800 transition-all group"
                    data-testid="quick-action-new-quote"
                  >
                    <div className="p-3 bg-purple-500/20 rounded-xl w-fit mb-3 group-hover:bg-purple-500/30 transition-colors">
                      <FileText className="w-5 h-5 text-purple-400" />
                    </div>
                    <h3 className="text-white font-semibold mb-1">Create New Quote</h3>
                    <p className="text-slate-400 text-sm">Draft a new quote for a client</p>
                  </button>
                  <button
                    onClick={() => { setActiveTab('invoices'); setDocumentModal({ type: 'invoice' }); }}
                    className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 text-left hover:border-purple-500/50 hover:bg-slate-800 transition-all group"
                    data-testid="quick-action-new-invoice"
                  >
                    <div className="p-3 bg-purple-500/20 rounded-xl w-fit mb-3 group-hover:bg-purple-500/30 transition-colors">
                      <Receipt className="w-5 h-5 text-purple-400" />
                    </div>
                    <h3 className="text-white font-semibold mb-1">Create New Invoice</h3>
                    <p className="text-slate-400 text-sm">Issue a new invoice to a client</p>
                  </button>
                </div>

                {/* Invoice Chart */}
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5" data-testid="invoice-chart">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-semibold">Invoices</h3>
                    <div className="flex gap-1 bg-slate-900/50 rounded-lg p-0.5 border border-slate-700">
                      {[
                        { key: '3m', label: '3M' },
                        { key: '6m', label: '6M' },
                        { key: '12m', label: '12M' },
                        { key: '5y', label: '5Y' },
                      ].map(opt => (
                        <button
                          key={opt.key}
                          onClick={() => setInvoiceChartPeriod(opt.key)}
                          className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                            invoiceChartPeriod === opt.key
                              ? 'bg-purple-600 text-white'
                              : 'text-slate-400 hover:text-slate-200'
                          }`}
                          data-testid={`chart-period-${opt.key}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {invoiceChartData.length === 0 ? (
                    <div className="flex items-center justify-center h-64 text-slate-500 text-sm">No invoice data for this period</div>
                  ) : (
                    <InvoiceChart data={invoiceChartData} />
                  )}
                </div>

                {/* Quotes Chart */}
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5" data-testid="quote-chart">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-semibold">Quotes</h3>
                    <div className="flex gap-1 bg-slate-900/50 rounded-lg p-0.5 border border-slate-700">
                      {[
                        { key: '3m', label: '3M' },
                        { key: '6m', label: '6M' },
                        { key: '12m', label: '12M' },
                        { key: '5y', label: '5Y' },
                      ].map(opt => (
                        <button
                          key={opt.key}
                          onClick={() => setQuoteChartPeriod(opt.key)}
                          className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                            quoteChartPeriod === opt.key
                              ? 'bg-purple-600 text-white'
                              : 'text-slate-400 hover:text-slate-200'
                          }`}
                          data-testid={`quote-chart-period-${opt.key}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {quoteChartData.length === 0 ? (
                    <div className="flex items-center justify-center h-64 text-slate-500 text-sm">No quote data for this period</div>
                  ) : (
                    <QuoteChart data={quoteChartData} />
                  )}
                </div>
              </div>
            )}

            {/* ==================== QUOTES ==================== */}
            {activeTab === 'quotes' && (
              <div className="space-y-4" data-testid="quotes-tab">
                {dashboard && (
                  <div className="grid grid-cols-1 gap-4">
                    <StatCard label="Outstanding Quotes" value={formatCurrency(dashboard.quote_stats?.sent?.total_value || 0)} icon={FileText} color="blue" badge={dashboard.quote_stats?.sent?.count || 0} />
                  </div>
                )}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="relative flex-1 max-w-sm">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                      <input className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder-slate-500" placeholder="Search quotes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="quotes-search" />
                    </div>
                    <select className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="quotes-status-filter">
                      <option value="">All Statuses</option>
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="accepted">Accepted</option>
                      <option value="declined">Declined</option>
                      <option value="expired">Expired</option>
                    </select>
                    <select className={`bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white ${siteFilter ? 'opacity-50 cursor-not-allowed' : ''}`} value={clientFilter} onChange={(e) => { setClientFilter(e.target.value); setSiteFilter(''); }} disabled={!!siteFilter} data-testid="quotes-client-filter">
                      <option value="">All Clients</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.company_name || `${c.first_name} ${c.last_name}`}</option>)}
                    </select>
                    <select className={`bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white ${clientFilter ? 'opacity-50 cursor-not-allowed' : ''}`} value={siteFilter} onChange={(e) => { setSiteFilter(e.target.value); setClientFilter(''); }} disabled={!!clientFilter} data-testid="quotes-site-filter">
                      <option value="">All Sites</option>
                      {sites.map(s => <option key={s.id} value={s.id}>{s.site_name}</option>)}
                    </select>
                    <div className="flex gap-0.5 bg-slate-900/50 rounded-lg p-0.5 border border-slate-700" data-testid="quotes-period-filter">
                      {[{ key: '', label: 'All' }, { key: '3m', label: '3M' }, { key: '6m', label: '6M' }, { key: '12m', label: '12M' }, { key: '5y', label: '5Y' }].map(opt => (
                        <button key={opt.key} onClick={() => setPeriodFilter(opt.key)} className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${periodFilter === opt.key ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-slate-200'}`} data-testid={`quotes-period-${opt.key || 'all'}`}>{opt.label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setSetupModal(true)} className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm font-medium transition-colors" data-testid="setup-btn-quotes">
                      <Settings className="w-4 h-4" /> Setup & Templates
                    </button>
                    <button onClick={() => setDocumentModal({ type: 'quote' })} className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors" data-testid="new-quote-btn">
                      <Plus className="w-4 h-4" /> New Quote
                    </button>
                  </div>
                </div>

                {quotes.length === 0 ? (
                  <EmptyState icon={FileText} title="No quotes found" description="Create your first quote to get started" action={<button onClick={() => setDocumentModal({ type: 'quote' })} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm">Create Quote</button>} />
                ) : (
                  <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-700/30">
                        <th className="text-left px-4 py-3 text-slate-400 font-medium">Quote #</th>
                        <th className="text-left px-4 py-3 text-slate-400 font-medium">Site</th>
                        <th className="text-left px-4 py-3 text-slate-400 font-medium">Client</th>
                        <th className="text-left px-4 py-3 text-slate-400 font-medium">Reference</th>
                        <th className="text-center px-4 py-3 text-slate-400 font-medium">Version</th>
                        <th className="text-right px-4 py-3 text-slate-400 font-medium">Total</th>
                        <th className="text-center px-4 py-3 text-slate-400 font-medium">Status</th>
                        <th className="text-right px-4 py-3 text-slate-400 font-medium">Date</th>
                      </tr></thead>
                      <tbody>
                        {quotes.map(q => (
                          <tr key={q.id} onClick={() => setDetailModal({ type: 'quote', doc: q })} className="border-t border-slate-700/30 hover:bg-slate-700/20 cursor-pointer transition-colors" data-testid={`quote-row-${q.id}`}>
                            <td className="px-4 py-3 text-white font-medium">{q.quote_number}</td>
                            <td className="px-4 py-3 text-slate-400">{sites.find(s => s.id === q.site_id)?.site_name || '-'}</td>
                            <td className="px-4 py-3 text-slate-300">{q.client_snapshot?.company_name || '-'}</td>
                            <td className="px-4 py-3 text-slate-400">{q.reference || '-'}</td>
                            <td className="px-4 py-3 text-slate-400 text-center">v{q.version}</td>
                            <td className="px-4 py-3 text-white font-medium text-right">{formatCurrency(q.total)}</td>
                            <td className="px-4 py-3 text-center"><StatusBadge status={q.status} /></td>
                            <td className="px-4 py-3 text-slate-400 text-right">{formatDate(q.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ==================== INVOICES ==================== */}
            {activeTab === 'invoices' && (
              <div className="space-y-4" data-testid="invoices-tab">
                {dashboard && (
                  <div className="grid grid-cols-2 gap-4">
                    <StatCard label="Outstanding Invoices" value={formatCurrency(dashboard.invoice_stats?.sent?.total_value || 0)} icon={Receipt} color="amber" badge={dashboard.invoice_stats?.sent?.count || 0} />
                    <StatCard label="Overdue" value={formatCurrency(dashboard.invoice_stats?.overdue?.total_value || 0)} icon={AlertTriangle} color="red" badge={dashboard.invoice_stats?.overdue?.count || 0} />
                  </div>
                )}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="relative flex-1 max-w-sm">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                      <input className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder-slate-500" placeholder="Search invoices..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="invoices-search" />
                    </div>
                    <select className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="invoices-status-filter">
                      <option value="">All Statuses</option>
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                      <option value="void">Void</option>
                    </select>
                    <select className={`bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white ${siteFilter ? 'opacity-50 cursor-not-allowed' : ''}`} value={clientFilter} onChange={(e) => { setClientFilter(e.target.value); setSiteFilter(''); }} disabled={!!siteFilter} data-testid="invoices-client-filter">
                      <option value="">All Clients</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.company_name || `${c.first_name} ${c.last_name}`}</option>)}
                    </select>
                    <select className={`bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white ${clientFilter ? 'opacity-50 cursor-not-allowed' : ''}`} value={siteFilter} onChange={(e) => { setSiteFilter(e.target.value); setClientFilter(''); }} disabled={!!clientFilter} data-testid="invoices-site-filter">
                      <option value="">All Sites</option>
                      {sites.map(s => <option key={s.id} value={s.id}>{s.site_name}</option>)}
                    </select>
                    <div className="flex gap-0.5 bg-slate-900/50 rounded-lg p-0.5 border border-slate-700" data-testid="invoices-period-filter">
                      {[{ key: '', label: 'All' }, { key: '3m', label: '3M' }, { key: '6m', label: '6M' }, { key: '12m', label: '12M' }, { key: '5y', label: '5Y' }].map(opt => (
                        <button key={opt.key} onClick={() => setPeriodFilter(opt.key)} className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${periodFilter === opt.key ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-slate-200'}`} data-testid={`invoices-period-${opt.key || 'all'}`}>{opt.label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setSetupModal(true)} className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm font-medium transition-colors" data-testid="setup-btn-invoices">
                      <Settings className="w-4 h-4" /> Setup & Templates
                    </button>
                    <button onClick={() => setDocumentModal({ type: 'invoice' })} className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors" data-testid="new-invoice-btn">
                      <Plus className="w-4 h-4" /> New Invoice
                    </button>
                  </div>
                </div>

                {invoices.length === 0 ? (
                  <EmptyState icon={Receipt} title="No invoices found" description="Create your first invoice or convert a quote" action={<button onClick={() => setDocumentModal({ type: 'invoice' })} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm">Create Invoice</button>} />
                ) : (
                  <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-700/30">
                        <th className="text-left px-4 py-3 text-slate-400 font-medium">Invoice #</th>
                        <th className="text-left px-4 py-3 text-slate-400 font-medium">Site</th>
                        <th className="text-left px-4 py-3 text-slate-400 font-medium">Client</th>
                        <th className="text-left px-4 py-3 text-slate-400 font-medium">Reference</th>
                        <th className="text-right px-4 py-3 text-slate-400 font-medium">Total</th>
                        <th className="text-center px-4 py-3 text-slate-400 font-medium">Status</th>
                        <th className="text-right px-4 py-3 text-slate-400 font-medium">Due Date</th>
                      </tr></thead>
                      <tbody>
                        {invoices.map(inv => (
                          <tr key={inv.id} onClick={() => setDetailModal({ type: 'invoice', doc: inv })} className="border-t border-slate-700/30 hover:bg-slate-700/20 cursor-pointer transition-colors" data-testid={`invoice-row-${inv.id}`}>
                            <td className="px-4 py-3 text-white font-medium">{inv.invoice_number}</td>
                            <td className="px-4 py-3 text-slate-400">{sites.find(s => s.id === inv.site_id)?.site_name || '-'}</td>
                            <td className="px-4 py-3 text-slate-300">{inv.client_snapshot?.company_name || '-'}</td>
                            <td className="px-4 py-3 text-slate-400">{inv.reference || '-'}</td>
                            <td className="px-4 py-3 text-white font-medium text-right">{formatCurrency(inv.total)}</td>
                            <td className="px-4 py-3 text-center"><StatusBadge status={inv.status} /></td>
                            <td className="px-4 py-3 text-slate-400 text-right">{formatDate(inv.due_date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

          </>
        )}
      </div>

      {/* ==================== MODALS ==================== */}
      {documentModal && (
        <DocumentFormModal
          doc={documentModal.doc}
          docType={documentModal.type}
          clients={clients}
          sites={sites}
          orgData={orgData}
          quotingSettings={quotingSettings}
          templates={templates.filter(t => t.type === documentModal.type)}
          onSaveTemplate={handleSaveTemplate}
          onSave={handleSaveDocument}
          onClose={() => setDocumentModal(null)}
        />
      )}

      {detailModal && (
        <DocumentDetailModal
          doc={detailModal.doc}
          docType={detailModal.type}
          orgData={orgData}
          sites={sites}
          pdfLoading={pdfLoading}
          onClose={() => setDetailModal(null)}
          onStatusChange={handleStatusChange}
          onConvertToInvoice={handleConvertToInvoice}
          onDownloadPdf={handleDownloadPdf}
          onDuplicate={handleDuplicate}
          onEdit={(doc) => { setDetailModal(null); setDocumentModal({ type: detailModal.type, doc }); }}
          onDelete={handleDeleteDocument}
        />
      )}

      {setupModal && (
        <SetupModal
          settings={quotingSettings}
          templates={templates}
          onSave={handleSaveSettings}
          onSaveTemplate={handleSaveTemplate}
          onDeleteTemplate={handleDeleteTemplate}
          onClose={() => setSetupModal(false)}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => confirmAction?.onConfirm?.()}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmText={confirmAction?.variant === 'danger' ? 'Delete' : 'Confirm'}
        variant={confirmAction?.variant || 'info'}
      />

      <ErrorModal
        isOpen={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        title={errorMessage?.title || 'Error'}
        message={errorMessage?.message || ''}
      />
    </div>
  );
};

export default AdminQuotingPage;
